/**
 * Networking Scripts Data
 * LinkedIn outreach templates and networking strategies
 */

import { CareerPath } from './careerPaths';

export interface NetworkingScript {
  scenario: string;
  description: string;
  template: string;
  customizationTips: string[];
  followUpScript?: string;
}

export interface NetworkingTip {
  category: 'connection' | 'content' | 'engagement' | 'follow-up';
  title: string;
  content: string;
}

export interface NetworkingData {
  connectionRequests: NetworkingScript[];
  informationalInterviews: NetworkingScript[];
  followUpMessages: NetworkingScript[];
  tips: NetworkingTip[];
  doAndDont: {
    do: string[];
    dont: string[];
  };
  targetPeople: {
    role: string;
    why: string;
  }[];
}

// Career-specific networking data
const NETWORKING_DATA: Record<string, NetworkingData> = {
  'software-developer': {
    connectionRequests: [
      {
        scenario: 'Connecting with a Developer at Target Company',
        description: 'Reaching out to someone in the role you want',
        template: `Hi [Name],

I noticed you're a [Role] at [Company] - I've been really impressed by [specific project/product they worked on]. I'm currently transitioning into software development and focusing on [your tech stack].

Would love to connect and learn about your experience. No pressure for a call - even a few insights would be incredibly helpful!

Best,
[Your name]`,
        customizationTips: [
          'Mention a specific project or blog post they worked on',
          'Show you\'ve done research - don\'t be generic',
          'Keep it under 300 characters',
          'Be specific about what you want to learn'
        ]
      },
      {
        scenario: 'Connecting with a Hiring Manager',
        description: 'Reaching out to someone who hires developers',
        template: `Hi [Name],

I saw [Company] is growing their engineering team - congrats on the [recent achievement/funding/product launch]! I'm a developer specializing in [your skills], particularly excited about [aspect of their product].

Would love to connect and stay on your radar for future opportunities.

Best,
[Your name]`,
        customizationTips: [
          'Reference recent company news or achievements',
          'Don\'t ask for a job directly - focus on connection',
          'Show genuine interest in their product',
          'Keep the door open for future conversations'
        ]
      }
    ],
    informationalInterviews: [
      {
        scenario: 'Requesting an Informational Interview',
        description: 'Asking a senior developer for career advice',
        template: `Hi [Name],

Thank you for connecting! I've been following your work on [specific project/content] and really admire how you [specific achievement].

I'm currently [your situation] and transitioning into [target role]. Would you be open to a 20-minute call to share some insights about:
- How you broke into [their specialty]
- What skills are most valuable for someone starting out
- Any advice for standing out as a candidate

I know your time is valuable - happy to work around your schedule. Coffee's on me if you're local!

Best,
[Your name]`,
        customizationTips: [
          'Be specific about what you want to learn',
          'Keep the ask small (15-20 minutes)',
          'Offer flexibility on timing',
          'Show you\'ve researched them specifically'
        ],
        followUpScript: `Hi [Name],

Thank you so much for the call last week - your advice about [specific advice] was exactly what I needed to hear.

I wanted to let you know I've already [action you took based on advice]. Looking forward to keeping in touch as I progress on this journey.

Best,
[Your name]`
      }
    ],
    followUpMessages: [
      {
        scenario: 'Following Up After an Event',
        description: 'Reconnecting after a meetup or conference',
        template: `Hi [Name],

Great connecting at [Event name] last [day]! I really enjoyed our conversation about [topic discussed].

I'd love to continue our discussion about [specific topic]. Are you free for a quick coffee chat sometime this week?

Best,
[Your name]`,
        customizationTips: [
          'Send within 24-48 hours of meeting',
          'Reference something specific you discussed',
          'Propose a concrete next step'
        ]
      }
    ],
    tips: [
      {
        category: 'connection',
        title: 'The 30-60-90 Rule',
        content: 'Connect with 30 people in your target role, 60 in adjacent roles, and 90 in your industry. Quantity matters, but quality of engagement matters more.'
      },
      {
        category: 'content',
        title: 'Share Your Learning Journey',
        content: 'Post about projects you\'re building, tutorials you\'ve completed, or problems you\'ve solved. This attracts opportunities and demonstrates your skills.'
      },
      {
        category: 'engagement',
        title: 'Comment Thoughtfully',
        content: 'Leave meaningful comments on posts from developers at companies you\'re targeting. This builds visibility before you reach out directly.'
      },
      {
        category: 'follow-up',
        title: 'The 3-Touch Rule',
        content: 'Plan to touch base 3 times: initial connection, a value-add message (article, resource), then an ask. This builds relationship before requesting.'
      }
    ],
    doAndDont: {
      do: [
        'Personalize every message - mention specific projects or posts',
        'Keep messages under 300 characters for connection requests',
        'Follow up within 48 hours of connecting',
        'Share relevant content and engage with their posts',
        'Be clear about what you\'re asking for',
        'Express gratitude and follow through on advice'
      ],
      dont: [
        'Send generic "I\'d like to add you to my network" messages',
        'Immediately ask for a job or referral',
        'Send long paragraphs about yourself',
        'Ghost after they\'ve helped you',
        'Connect and then never engage',
        'Be pushy or send multiple follow-ups in a week'
      ]
    },
    targetPeople: [
      { role: 'Senior Software Engineers', why: 'Can share career path and mentorship' },
      { role: 'Engineering Managers', why: 'Hiring authority and team insight' },
      { role: 'Developer Advocates', why: 'Often helpful and well-connected' },
      { role: 'Tech Recruiters', why: 'Access to multiple opportunities' },
      { role: 'Career Changers Who Made It', why: 'Relatable advice for your journey' }
    ]
  },

  'project-manager': {
    connectionRequests: [
      {
        scenario: 'Connecting with a PM at Target Company',
        description: 'Reaching out to a product or project manager',
        template: `Hi [Name],

I came across your profile while researching [Company] - I was impressed by [specific achievement or project]. I'm transitioning into project management with a background in [your background].

Would love to connect and learn about your experience leading [type of projects] at [Company].

Best,
[Your name]`,
        customizationTips: [
          'Mention their specific project or team',
          'Highlight your relevant background',
          'Show genuine interest in their work'
        ]
      }
    ],
    informationalInterviews: [
      {
        scenario: 'Requesting PM Career Advice',
        description: 'Learning about the PM career path',
        template: `Hi [Name],

Thanks for connecting! I've been following your work at [Company] and really admire how you've [specific achievement - shipped product, led team, etc.].

I'm transitioning from [your background] to project management and would love 15 minutes of your time to learn:
- How you broke into PM without a traditional PM background
- What certifications or skills made the biggest difference
- How you demonstrate PM skills in interviews

Happy to work around your schedule - really appreciate any time you can spare!

Best,
[Your name]`,
        customizationTips: [
          'Reference their career path if similar to yours',
          'Ask about certifications strategically',
          'Show you understand the PM skill set'
        ]
      }
    ],
    followUpMessages: [],
    tips: [
      {
        category: 'content',
        title: 'Document Your Leadership',
        content: 'Share stories about how you\'ve led cross-functional work, resolved conflicts, or delivered results. PMs are always teaching and documenting.'
      },
      {
        category: 'engagement',
        title: 'Join PM Communities',
        content: 'Engage in communities like Product Coalition, Mind the Product, or PMI chapters. Active participation builds credibility.'
      }
    ],
    doAndDont: {
      do: [
        'Share case studies of projects you\'ve managed',
        'Engage with content about PM methodologies',
        'Connect with people across functions (engineering, design, business)',
        'Ask thoughtful questions about their PM journey'
      ],
      dont: [
        'Oversell your experience - be honest about your level',
        'Focus only on certifications over real experience',
        'Ignore the importance of soft skills in networking'
      ]
    },
    targetPeople: [
      { role: 'Senior Project Managers', why: 'Career path and methodology insights' },
      { role: 'PMO Directors', why: 'Understanding organizational PM needs' },
      { role: 'Scrum Masters', why: 'Agile methodology expertise' },
      { role: 'Product Managers', why: 'Adjacent role and collaboration insights' }
    ]
  },

  'data-analyst': {
    connectionRequests: [
      {
        scenario: 'Connecting with a Data Professional',
        description: 'Reaching out to someone in data analytics',
        template: `Hi [Name],

I saw your work on [specific project/dashboard/analysis] - really impressive how you [specific insight]. I'm building my data analytics skills in [SQL/Python/Tableau] and transitioning from [your background].

Would love to connect and learn about your experience in data at [Company].

Best,
[Your name]`,
        customizationTips: [
          'Reference specific technical work if you can find it',
          'Mention the tools you\'re learning',
          'Show you understand what data analysts do'
        ]
      }
    ],
    informationalInterviews: [
      {
        scenario: 'Learning About Data Career Paths',
        description: 'Understanding analyst vs scientist paths',
        template: `Hi [Name],

Thanks for connecting! I've been learning [SQL/Python/visualization tools] and I'm excited about data careers, but honestly a bit confused about the Analyst vs Scientist vs Engineer paths.

Would you have 15 minutes to help me understand:
- What a typical day looks like for you
- How you'd advise someone to break in
- What skills actually matter most day-to-day

Really appreciate any insights you can share!

Best,
[Your name]`,
        customizationTips: [
          'Be honest about what you\'re trying to understand',
          'Ask about day-to-day work, not just job requirements',
          'Show you\'re actively learning relevant skills'
        ]
      }
    ],
    followUpMessages: [],
    tips: [
      {
        category: 'content',
        title: 'Share Your Analysis Projects',
        content: 'Post about datasets you\'ve analyzed, visualizations you\'ve created, or insights you\'ve discovered. Show your thinking process, not just results.'
      },
      {
        category: 'engagement',
        title: 'Engage with Data Content',
        content: 'Comment on posts about SQL, Python, or visualization tools. Share resources you\'ve found helpful. Build a reputation as someone who contributes.'
      }
    ],
    doAndDont: {
      do: [
        'Share links to portfolio projects or GitHub',
        'Ask specific technical questions that show learning',
        'Engage with data community hashtags',
        'Connect with people at companies using data you find interesting'
      ],
      dont: [
        'Pretend to know more than you do',
        'Skip fundamentals - show you understand basics',
        'Only focus on tools, ignore the business context'
      ]
    },
    targetPeople: [
      { role: 'Senior Data Analysts', why: 'Career path and technical mentorship' },
      { role: 'Analytics Managers', why: 'Hiring perspective and team needs' },
      { role: 'Data Scientists', why: 'Understanding the growth path' },
      { role: 'Business Intelligence Analysts', why: 'Visualization and reporting focus' }
    ]
  }
};

// Generic networking data
const GENERIC_NETWORKING: NetworkingData = {
  connectionRequests: [
    {
      scenario: 'General Professional Connection',
      description: 'Connecting with someone in your target field',
      template: `Hi [Name],

I came across your profile while researching [their field/company]. I'm really impressed by your work in [specific area] and I'm currently [your situation - transitioning to/building skills in] this field.

Would love to connect and learn from your experience.

Best,
[Your name]`,
      customizationTips: [
        'Always personalize with something specific about them',
        'Keep it short - under 300 characters if possible',
        'Be clear about why you want to connect',
        'Don\'t ask for anything in the initial request'
      ]
    },
    {
      scenario: 'Connecting via Mutual Interest',
      description: 'Bonding over shared interests or groups',
      template: `Hi [Name],

I noticed we're both part of [group/attended same event/share interest]. Really enjoyed your post about [specific topic].

Would love to connect and continue the conversation about [shared interest].

Best,
[Your name]`,
      customizationTips: [
        'Reference the specific shared connection',
        'Show you\'ve engaged with their content',
        'Propose continuing a conversation, not starting cold'
      ]
    }
  ],
  informationalInterviews: [
    {
      scenario: 'General Informational Interview Request',
      description: 'Asking for career advice from someone in your target role',
      template: `Hi [Name],

Thanks for connecting! I've been researching careers in [field] and your path really resonates with me, especially [specific aspect of their career].

I'm currently [your situation] and working toward [your goal]. Would you have 15-20 minutes for a quick call? I'd love to learn:
- What you wish you'd known when starting out
- How you'd recommend breaking into the field today
- What skills or experiences matter most

Happy to work around your schedule - really appreciate any time you can spare!

Best,
[Your name]`,
      customizationTips: [
        'Be specific about what you want to learn',
        'Keep the time ask small (15-20 minutes max)',
        'Show you\'ve researched their background',
        'Offer scheduling flexibility'
      ],
      followUpScript: `Hi [Name],

Thank you so much for taking the time to chat yesterday! Your advice about [specific insight] was incredibly helpful.

I've already [action you\'ve taken based on their advice]. I\'ll keep you posted on my progress, and please don\'t hesitate to reach out if there\'s ever anything I can help you with.

Best,
[Your name]`
    }
  ],
  followUpMessages: [
    {
      scenario: 'Thank You After Help',
      description: 'Following up after someone helps you',
      template: `Hi [Name],

I wanted to follow up and share that [update on your progress]. Your advice about [specific advice] really helped me [specific outcome].

Thank you again for your generosity with your time. I hope I can return the favor someday!

Best,
[Your name]`,
      customizationTips: [
        'Be specific about how they helped',
        'Share your progress - people love seeing impact',
        'Keep the relationship warm for future contact'
      ]
    },
    {
      scenario: 'Staying in Touch',
      description: 'Periodic check-in to maintain relationships',
      template: `Hi [Name],

I was reading about [something relevant to them - company news, industry trend] and thought of our conversation about [topic].

Just wanted to check in and say hi! I've been [your progress update]. Hope things are going well with [something specific to them].

Best,
[Your name]`,
      customizationTips: [
        'Have a reason to reach out (article, news, achievement)',
        'Share an update on your own progress',
        'Keep it brief and genuine'
      ]
    }
  ],
  tips: [
    {
      category: 'connection',
      title: 'Quality Over Quantity',
      content: 'It\'s better to have 100 connections you actually engage with than 5000 you\'ve never spoken to. Focus on building real relationships.'
    },
    {
      category: 'content',
      title: 'Give Before You Ask',
      content: 'Share valuable content, comment helpfully on others\' posts, and build goodwill before asking for anything. Networking is about giving, not just taking.'
    },
    {
      category: 'engagement',
      title: 'Engage Consistently',
      content: 'Spend 15-20 minutes daily engaging on LinkedIn. Comment thoughtfully on posts from people at your target companies.'
    },
    {
      category: 'follow-up',
      title: 'Always Follow Up',
      content: 'If someone helps you, follow up to thank them and share your progress. This turns a one-time conversation into an ongoing relationship.'
    }
  ],
  doAndDont: {
    do: [
      'Personalize every message with something specific',
      'Keep connection requests short and friendly',
      'Follow up within 48 hours of connecting',
      'Share your progress with people who\'ve helped you',
      'Engage with content before reaching out directly',
      'Be genuine and authentic in your communications'
    ],
    dont: [
      'Send generic connection requests',
      'Ask for a job in your first message',
      'Write long paragraphs about yourself',
      'Forget to follow up after meetings',
      'Only reach out when you need something',
      'Be pushy or send multiple messages in a short period'
    ]
  },
  targetPeople: [
    { role: 'People in Your Target Role', why: 'Learn the day-to-day and path to get there' },
    { role: 'Hiring Managers', why: 'Understand what they look for in candidates' },
    { role: 'Recruiters in Your Field', why: 'Access to multiple opportunities' },
    { role: 'Career Changers Who Succeeded', why: 'Relatable advice and inspiration' },
    { role: 'Thought Leaders', why: 'Visibility and industry insights' }
  ]
};

/**
 * Get networking data for a career
 */
export function getNetworkingData(career: CareerPath): NetworkingData {
  const careerId = career.id.toLowerCase();

  // Check for exact match
  if (NETWORKING_DATA[careerId]) {
    return NETWORKING_DATA[careerId];
  }

  // Check for partial matches
  for (const key of Object.keys(NETWORKING_DATA)) {
    if (careerId.includes(key) || key.includes(careerId)) {
      return NETWORKING_DATA[key];
    }
  }

  // Return generic data
  return GENERIC_NETWORKING;
}
